import {
  ContentService,
  FlashcardDecksService,
  FlashcardsService,
} from "@/client"
import { Checkbox } from "@/components/ui/checkbox"
import { useColorModeValue } from "@/components/ui/color-mode"
import { Tooltip } from "@/components/ui/tooltip"
import { Box, Button, Flex, HStack, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

const BAND_LABELS: {
  value: string
  label: string
  meaning: string
  description: string
}[] = [
  {
    value: "new",
    label: "New",
    meaning: "Unrated",
    description: "Cards you have not rated yet.",
  },
  {
    value: "1",
    label: "1",
    meaning: "Not at all",
    description: "You did not know the answer.",
  },
  {
    value: "2",
    label: "2",
    meaning: "Barely",
    description: "You barely remembered it.",
  },
  {
    value: "3",
    label: "3",
    meaning: "Somewhat",
    description: "You partly knew it.",
  },
  {
    value: "4",
    label: "4",
    meaning: "Mostly",
    description: "You mostly knew it.",
  },
  {
    value: "5",
    label: "5",
    meaning: "Perfectly",
    description: "You knew it perfectly.",
  },
]

const SUBJECT_COLOR_PALETTES = [
  "blue",
  "purple",
  "orange",
  "cyan",
  "pink",
  "green",
  "teal",
  "red",
  "amber",
  "yellow",
  "lime",
  "emerald",
  "indigo",
  "fuchsia",
  "rose",
] as const

type ColorPalette =
  | (typeof SUBJECT_COLOR_PALETTES)[number]
  | "gray"
  | "red"
  | "yellow"

export function getSubjectColorPalette(
  subjectId: string | null | undefined,
  subjectIds: string[],
): ColorPalette {
  if (!subjectId) return "gray"
  const index = subjectIds.indexOf(subjectId)
  return index === -1
    ? "gray"
    : SUBJECT_COLOR_PALETTES[index % SUBJECT_COLOR_PALETTES.length]
}

export function getBandColorPalette(value: string): ColorPalette {
  const palettes: Record<string, ColorPalette> = {
    new: "gray",
    "1": "red",
    "2": "orange",
    "3": "yellow",
    "4": "green",
    "5": "teal",
  }
  return palettes[value] ?? "gray"
}

function getBandCount(
  bandSummary:
    | {
        new?: number
        band_1?: number
        band_2?: number
        band_3?: number
        band_4?: number
        band_5?: number
      }
    | undefined,
  value: string,
): number {
  if (!bandSummary) return 0
  if (value === "new") return bandSummary.new ?? 0
  const key = `band_${value}` as keyof typeof bandSummary
  return (bandSummary[key] as number | undefined) ?? 0
}

export function CustomStudyPanel() {
  const navigate = useNavigate()
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const cardBg = useColorModeValue("white", "gray.900")

  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([])
  const [selectedDeckIds, setSelectedDeckIds] = useState<string[]>([])
  const [selectedBands, setSelectedBands] = useState<string[]>([])
  const [shuffleQuestions, setShuffleQuestions] = useState(false)

  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
  })

  const { data: deckSubjectsData } = useQuery({
    queryKey: ["flashcard-deck-subjects"],
    queryFn: () => FlashcardDecksService.listDeckSubjects(),
  })

  const selectedSubjectIdsKey = [...selectedSubjectIds].sort()
  const { data: decksData } = useQuery({
    queryKey: ["flashcard-decks", "custom-study", selectedSubjectIdsKey],
    queryFn: () =>
      FlashcardDecksService.listDecks({ subjectIds: selectedSubjectIdsKey }),
    enabled: selectedSubjectIds.length > 0,
  })

  const { data: bandSummary } = useQuery({
    queryKey: ["flashcards-band-summary", selectedSubjectIds, selectedDeckIds],
    queryFn: () =>
      FlashcardsService.getBandSummary({
        subjectId: selectedSubjectIds,
        deckId: selectedDeckIds,
      }),
  })

  const subjects = (subjectsData?.data ?? []).filter((subject) =>
    deckSubjectsData?.subject_ids.includes(subject.id),
  )
  const subjectIds = subjects.map((subject) => subject.id)

  const selectableDecks = useMemo(() => {
    const decksWithCards = (decksData?.data ?? []).filter(
      (deck) => (deck.card_count ?? 0) > 0,
    )
    if (selectedSubjectIds.length === 0) {
      return []
    }
    return decksWithCards.filter((deck) =>
      getDeckSubjectIds(deck).some((subjectId) =>
        selectedSubjectIds.includes(subjectId),
      ),
    )
  }, [decksData?.data, selectedSubjectIds])

  useEffect(() => {
    const visibleDeckIds = new Set(selectableDecks.map((deck) => deck.id))
    setSelectedDeckIds((prev) => prev.filter((id) => visibleDeckIds.has(id)))
  }, [selectableDecks])

  function toggleSubject(id: string) {
    setSelectedSubjectIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    )
  }

  function toggleBand(value: string) {
    setSelectedBands((prev) =>
      prev.includes(value) ? prev.filter((b) => b !== value) : [...prev, value],
    )
  }

  function toggleDeck(id: string) {
    setSelectedDeckIds((prev) =>
      prev.includes(id)
        ? prev.filter((deckId) => deckId !== id)
        : [...prev, id],
    )
  }

  const canStart = selectedBands.length > 0
  const allBandsSelected = selectedBands.length === BAND_LABELS.length

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      borderColor={borderColor}
      bg={cardBg}
      p={{ base: 4, md: 5 }}
    >
      <VStack align="stretch" gap={5}>
        {subjects.length > 0 && (
          <Box>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={mutedText}
              textTransform="uppercase"
              letterSpacing="wider"
              mb={2}
            >
              Filter by subject (optional)
            </Text>
            <Flex gap={2} wrap="wrap">
              {subjects.map((subject) => {
                const isSelected = selectedSubjectIds.includes(subject.id)
                const colorPalette = getSubjectColorPalette(
                  subject.id,
                  subjectIds,
                )
                return (
                  <Button
                    key={subject.id}
                    size="xs"
                    borderRadius="full"
                    colorPalette={colorPalette}
                    variant={isSelected ? "solid" : "outline"}
                    data-color-palette={colorPalette}
                    onClick={() => toggleSubject(subject.id)}
                  >
                    {subject.name}
                  </Button>
                )
              })}
            </Flex>
          </Box>
        )}

        {selectableDecks.length > 0 && (
          <Box>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={mutedText}
              textTransform="uppercase"
              letterSpacing="wider"
              mb={2}
            >
              Filter by deck (optional)
            </Text>
            <Flex gap={2} wrap="wrap">
              {selectableDecks.map((deck) => {
                const isSelected = selectedDeckIds.includes(deck.id)
                const deckSubjectId = getDeckSubjectId(deck, selectedSubjectIds)
                const colorPalette = getSubjectColorPalette(
                  deckSubjectId,
                  subjectIds,
                )
                return (
                  <Button
                    key={deck.id}
                    size="xs"
                    borderRadius="full"
                    colorPalette={colorPalette}
                    variant={isSelected ? "solid" : "outline"}
                    data-color-palette={colorPalette}
                    onClick={() => toggleDeck(deck.id)}
                  >
                    {deck.name}
                  </Button>
                )
              })}
            </Flex>
          </Box>
        )}

        <Box>
          <HStack justify="space-between" align="center" mb={2}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={mutedText}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Rating bands
            </Text>
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                setSelectedBands(
                  allBandsSelected ? [] : BAND_LABELS.map(({ value }) => value),
                )
              }
            >
              {allBandsSelected ? "Clear all" : "Select all"}
            </Button>
          </HStack>
          <HStack gap={2} wrap="wrap">
            {BAND_LABELS.map(({ value, label, meaning, description }) => {
              const count = getBandCount(bandSummary, value)
              const isSelected = selectedBands.includes(value)
              const colorPalette = getBandColorPalette(value)
              return (
                <Tooltip key={value} content={description}>
                  <Button
                    size="xs"
                    borderRadius="full"
                    colorPalette={colorPalette}
                    variant={isSelected ? "solid" : "outline"}
                    data-color-palette={colorPalette}
                    onClick={() => toggleBand(value)}
                    title={description}
                    h="auto"
                    minH="32px"
                    py={1}
                  >
                    <HStack gap={1.5} minW={0}>
                      <Text as="span" fontWeight="bold" whiteSpace="nowrap">
                        {label}
                      </Text>
                      <Text as="span" fontSize="xs" whiteSpace="nowrap">
                        {meaning}
                      </Text>
                      <Text as="span" fontWeight="bold" whiteSpace="nowrap">
                        ({count})
                      </Text>
                    </HStack>
                  </Button>
                </Tooltip>
              )
            })}
          </HStack>
        </Box>

        <Checkbox
          checked={shuffleQuestions}
          onCheckedChange={(details) => setShuffleQuestions(!!details.checked)}
        >
          Shuffle questions
        </Checkbox>

        <Button
          colorPalette="teal"
          disabled={!canStart}
          onClick={() =>
            navigate({
              to: "/study/custom",
              search: {
                subjectIds: selectedSubjectIds,
                deckIds: selectedDeckIds,
                ratings: selectedBands.filter((b) => b !== "new").map(Number),
                includeNew: selectedBands.includes("new"),
                shuffle: shuffleQuestions,
              },
            })
          }
        >
          Start session
        </Button>
      </VStack>
    </Box>
  )
}

function getDeckSubjectId(
  deck: { subject_id?: string | null; subject_ids?: string[] },
  selectedSubjectIds: string[],
): string | null | undefined {
  const subjectIds = getDeckSubjectIds(deck)
  return (
    selectedSubjectIds.find((subjectId) => subjectIds.includes(subjectId)) ??
    subjectIds[0]
  )
}

function getDeckSubjectIds(deck: {
  subject_id?: string | null
  subject_ids?: string[]
}): string[] {
  return deck.subject_ids ?? (deck.subject_id ? [deck.subject_id] : [])
}

export default CustomStudyPanel
